import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { APP_TYPES } from "constants/AppTypes";
import { logout as logoutAction } from "actions/auth.js";

/**
 * Samsung TV (Tizen) remote-control handling + on-screen hint.
 *
 * The Samsung app runs the live site inside a local iframe shell (see
 * "Samsung Tv GameCity App"). window.tizen only exists in that outer shell, not
 * here on the remote page — so this component does NOT depend on window.tizen:
 *
 *   RED    -> log out (clear storage, back to /samsung lobby) — like Fire TV
 *   GREEN  -> refresh
 *   Return -> show the mandatory "Exit Game City?" confirmation popup. Only the
 *             shell can actually close the app (it owns window.tizen), so on
 *             confirm we postMessage `gc-exit` to the parent shell, which exits.
 *
 * The shell registers the colour keys (only it can) and forwards Return to us as
 * a `back` key so the popup renders here. When the iframe is focused the colour
 * keydowns arrive here directly; when the shell is focused it forwards them via
 * postMessage. We handle both. Active for the Samsung app type only — Fire TV /
 * Roku have their own native controls.
 */
const KEY_RED = 403;
const KEY_GREEN = 404;
const KEY_RETURN = 10009; // Tizen Return / Back
const KEY_ENTER = 13;
const KEY_LEFT = 37;
const KEY_RIGHT = 39;

export default function TizenRemote({ appType }) {
    const isSamsung = appType === APP_TYPES.SAMSUNG;
    // Mandatory Samsung exit-confirmation popup. exitFocused tracks which button
    // the D-pad is on (true = "Exit", false = "Stay").
    const [showExit, setShowExit] = useState(false);
    const [exitFocused, setExitFocused] = useState(true);
    // An in-app overlay (currently the privacy modal on the welcome screen) can
    // claim Return while it's open, so Return closes it instead of prompting to
    // exit the whole app. Overlays announce themselves on the window.
    const [overlayOpen, setOverlayOpen] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        const open = () => setOverlayOpen(true);
        const close = () => setOverlayOpen(false);
        window.addEventListener("gc-overlay-open", open);
        window.addEventListener("gc-overlay-close", close);
        return () => {
            window.removeEventListener("gc-overlay-open", open);
            window.removeEventListener("gc-overlay-close", close);
        };
    }, []);

    useEffect(() => {
        if (!isSamsung) return;

        const logout = () => {
            // Run the real logout so the session is ended SERVER-side (the action
            // POSTs api/Authenticate/logout, leaves the room if hosting, removes
            // the stored user, and resets Redux). Just clearing storage — what
            // this used to do — left the backend session alive, so the TV wasn't
            // fully logged out.
            try { dispatch(logoutAction()); } catch (e) { /* dispatch unavailable */ }
            // Belt-and-braces: clear anything else (cart, prefs) on the shared TV.
            try {
                localStorage.clear();
                sessionStorage.clear();
            } catch (e) { /* storage may be unavailable */ }
            // Delay the hard reload so the fire-and-forget logout POST flushes to
            // the server before navigation can abort it.
            setTimeout(() => { window.location.href = "/samsung"; }, 300);
        };
        const refresh = () => window.location.reload();

        // Only the Tizen shell holds window.tizen, so ask it to close the app.
        // Fall back to a direct exit in case we're ever running top-level.
        const doExit = () => {
            try { window.parent.postMessage({ type: "gc-exit" }, "*"); } catch (e) {}
            try { if (window.tizen) window.tizen.application.getCurrentApplication().exit(); } catch (e) {}
        };

        const act = (key) => {
            // An overlay owns Return while it's open — ask it to close rather
            // than offering to quit the app out from under it.
            if (overlayOpen && !showExit) {
                if (key === "back") window.dispatchEvent(new CustomEvent("gc-overlay-back"));
                return;
            }
            if (showExit) {
                // While the confirm popup is open, keys drive the popup only.
                if (key === "back") setShowExit(false);
                else if (key === "left") setExitFocused(true);
                else if (key === "right") setExitFocused(false);
                else if (key === "enter") { if (exitFocused) doExit(); else setShowExit(false); }
                return;
            }
            if (key === "red") logout();
            else if (key === "green") refresh();
            else if (key === "back") { setExitFocused(true); setShowExit(true); }
        };

        const onKeyDown = (e) => {
            switch (e.keyCode) {
                case KEY_RED: act("red"); break;
                case KEY_GREEN: act("green"); break;
                case KEY_RETURN: act("back"); break;
                // Only intercept D-pad / Enter while the popup is open, so the
                // game grid keeps full control of them the rest of the time.
                case KEY_ENTER: if (showExit) act("enter"); else return; break;
                case KEY_LEFT: if (showExit) act("left"); else return; break;
                case KEY_RIGHT: if (showExit) act("right"); else return; break;
                default: return;
            }
            e.preventDefault();
        };

        // When the outer shell has focus it forwards keys (incl. Return as `back`).
        const onMessage = (e) => {
            if (e.data && e.data.type === "gc-key") act(e.data.key);
        };

        document.addEventListener("keydown", onKeyDown);
        window.addEventListener("message", onMessage);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("message", onMessage);
        };
    }, [isSamsung, showExit, exitFocused, overlayOpen, dispatch]);

    if (!isSamsung) return null;

    const btnBase = {
        padding: "12px 32px",
        fontSize: "22px",
        borderRadius: "8px",
        border: "2px solid transparent",
        cursor: "pointer",
    };
    const btnFocused = { ...btnBase, background: "#fff", color: "#111", borderColor: "#fff" };
    const btnNormal = { ...btnBase, background: "rgba(255,255,255,0.12)", color: "#fff", borderColor: "rgba(255,255,255,0.4)" };

    return (
        <>
            {/* On-screen control hint */}
            <div
                style={{
                    position: "fixed",
                    bottom: "16px",
                    left: "20px",
                    zIndex: 9999,
                    display: "flex",
                    gap: "20px",
                    padding: "8px 16px",
                    borderRadius: "10px",
                    background: "rgba(0, 0, 0, 0.6)",
                    color: "#fff",
                    fontSize: "20px",
                    pointerEvents: "none",
                }}
            >
                <span>🔴 Log&nbsp;out</span>
                <span>🟢 Refresh</span>
                <span>↩ Exit</span>
            </div>

            {/* Mandatory Samsung exit-confirmation popup (RETURN / EXIT) */}
            {showExit && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 10000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(0, 0, 0, 0.7)",
                    }}
                >
                    <div
                        style={{
                            background: "#1c1c28",
                            padding: "40px 48px",
                            borderRadius: "16px",
                            textAlign: "center",
                            boxShadow: "0 12px 48px rgba(0,0,0,0.6)",
                        }}
                    >
                        <p style={{ color: "#fff", fontSize: "30px", margin: "0 0 28px" }}>
                            Exit Game City?
                        </p>
                        <div style={{ display: "flex", gap: "20px", justifyContent: "center" }}>
                            <button
                                style={exitFocused ? btnFocused : btnNormal}
                                onClick={() => {
                                    try { window.parent.postMessage({ type: "gc-exit" }, "*"); } catch (e) {}
                                    try { if (window.tizen) window.tizen.application.getCurrentApplication().exit(); } catch (e) {}
                                }}
                            >
                                Exit
                            </button>
                            <button
                                style={!exitFocused ? btnFocused : btnNormal}
                                onClick={() => setShowExit(false)}
                            >
                                Stay
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
